import React, { useState, useEffect, useRef } from "react";
import {
  MDBCheckbox,
  MDBInput,
  MDBSideNavCollapse,
  MDBSideNavItem,
  MDBSideNavLink,
} from "mdb-react-ui-kit";
import { COMMITTEE_TYPE_LABELS } from "../../utils/committeeTypeLabels";

const CommitteesFilter = ({
  committeeFilterCollapse,
  setCommitteeFilterCollapse,
  allCommittees,
  selectedCommittees,
  setSelectedCommittees,
  selectedCommitteeTypes,
  setSelectedCommitteeTypes,
  onFilteredCommitteesChange,
  updateSelectedFiltersOrReporting,
}) => {
  const [initialized, setInitialized] = useState(false);
  const displayCommitteesRef = useRef([]);
  const [searchCommittee, setSearchCommittee] = useState("");

  useEffect(() => {
    if (!initialized && allCommittees.length > 0) {
      displayCommitteesRef.current = [...allCommittees];
      setInitialized(true);
    }
  }, [allCommittees, initialized]);

  const existingTypes = new Set(
    displayCommitteesRef.current
      .map((c) => c.committeeDetails?.committeeCode?.committeeType?.code)
      .filter(Boolean)
  );
  
  const allCommitteeTypes = Object.keys(COMMITTEE_TYPE_LABELS).filter((key) =>
    existingTypes.has(key)
  );

  // Toggle the unique key in the selectedCommittees state
  const handleCheckboxChange = (uniqueKey) => {
    if (selectedCommittees && selectedCommittees.includes(uniqueKey)) {
      updateSelectedFiltersOrReporting({selectedCommittees: selectedCommittees.filter((c) => c !== uniqueKey)})
      setSelectedCommittees(selectedCommittees.filter((c) => c !== uniqueKey));
    } else if (selectedCommittees) {
      updateSelectedFiltersOrReporting({selectedCommittees: [...selectedCommittees, uniqueKey]})
      setSelectedCommittees([...selectedCommittees, uniqueKey]);
    }
    else {
      updateSelectedFiltersOrReporting({selectedCommittees: [uniqueKey]})
      setSelectedCommittees([uniqueKey]);
    }
  };

  // Deduplicate committees based on the unique key (committee type + id)
  const uniqueCommittees = displayCommitteesRef.current.filter(
    (committee, index, self) => {
      const key = `${committee.sessionId ? "standing" : "nonStanding"}-${
        committee.id
      }`;
      return (
        index ===
        self.findIndex((c) => {
          const cKey = `${c.sessionId ? "standing" : "nonStanding"}-${c.id}`;
          return cKey === key;
        })
      );
    }
  );

  // Filter committees by search term
  const filteredCommittees = uniqueCommittees.filter((committee) => {
    const nameMatches = committee.committeeDetails.committeeCode.name
      .toLowerCase()
      .includes(searchCommittee.toLowerCase());

    const typeCode =
      committee.committeeDetails?.committeeCode?.committeeType?.code;

    const typeMatches =
      selectedCommitteeTypes.length === 0 ||
      selectedCommitteeTypes.includes(typeCode);

    return nameMatches && typeMatches;
  });

  // Helper to remove (H) or (S) from the beginning of the committee name
  const cleanCommitteeName = (committee) =>
    committee.committeeDetails.committeeCode.name
      .replace(/^\((H|S)\)\s*/, "")
      .toLowerCase();

  // Sort committees alphabetically by cleaned name
  const sortedCommittees = filteredCommittees.slice().sort((a, b) => {
    const nameA = cleanCommitteeName(a);
    const nameB = cleanCommitteeName(b);
    return nameA.localeCompare(nameB);
  });

  useEffect(() => {
    if (onFilteredCommitteesChange) {
      onFilteredCommitteesChange(filteredCommittees);
    }
  }, [filteredCommittees, onFilteredCommitteesChange]);

  return (
    <MDBSideNavItem>
      <MDBSideNavLink
        className="accordion-button"
        icon="angle-down"
        shouldBeExpanded={committeeFilterCollapse}
        onClick={() => setCommitteeFilterCollapse(!committeeFilterCollapse)}
      >
        Committees
      </MDBSideNavLink>
      <MDBSideNavCollapse open={committeeFilterCollapse}>
        <li className="p-3">
          <fieldset className="mb-3 d-flex justify-content-between">
            <legend>Type</legend>
            <div>
              {allCommitteeTypes.map((typeCode) => (
                <MDBCheckbox
                  key={typeCode}
                  label={COMMITTEE_TYPE_LABELS[typeCode] || typeCode}
                  checked={selectedCommitteeTypes.includes(typeCode)}
                  onChange={() => {
                    setSelectedCommitteeTypes((prev) => {
                      updateSelectedFiltersOrReporting({selectedCommitteeTypes: prev.includes(typeCode)
                        ? prev.filter((t) => t !== typeCode)
                        : [...prev, typeCode]})

                      return prev.includes(typeCode)
                      ? prev.filter((t) => t !== typeCode)
                      : [...prev, typeCode]
                      }
                    );
                  }}
                  className="mb-2"
                />
              ))}
            </div>
          </fieldset>
          <MDBInput
            type="text"
            label="Search Committees..."
            value={searchCommittee}
            onChange={(e) => setSearchCommittee(e.target.value)}
          />
          <div
            style={{
              maxHeight: "200px",
              overflowY: "auto",
              border: "1px solid #ddd",
              padding: "10px",
            }}
          >
            {sortedCommittees.map((committee) => {
              // Generate a unique key: for example, "standing-15" or "nonStanding-15"
              const uniqueKey = `${
                committee.sessionId ? "standing" : "nonStanding"
              }-${committee.id}`;
              return (
                <MDBCheckbox
                  key={uniqueKey}
                  label={`${committee.committeeDetails.committeeCode.name}`}
                  checked={selectedCommittees && selectedCommittees.includes(uniqueKey)}
                  onChange={() => handleCheckboxChange(uniqueKey)}
                />
              );
            })}
          </div>
        </li>
      </MDBSideNavCollapse>
    </MDBSideNavItem>
  );
};

export default CommitteesFilter;
